import ConfirmationDialog from '../../../../components/messages/ConfirmationDialog';
import LoadingButton from '../../../../components/inputs/LoadingButton';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useFormContext } from 'react-hook-form';
import {
  Claim,
  ClaimForm,
  UpdateClaimArgs,
} from '../../../../Interfaces/Insurance/Claim';
import { putClaim } from '../../../../services/Insurance/Claim';
import { useClaimSummaryContext } from './../ClaimContext';
import ClaimsForm from '../forms/ClaimForm';
import { ClaimFiltersOptions } from '../ClaimFilters';

type UpdateClaimProps = {
  id: string;
  afterUpdate: (claim: Claim) => void;
  filterStatus: ClaimFiltersOptions['claimStatus'];
  prevAttachment?: string;
};

const UpdateClaim = ({
  id,
  afterUpdate,
  filterStatus,
  prevAttachment,
}: UpdateClaimProps) => {
  const formMethods = useFormContext<ClaimForm>();
  const { updateOne, removeOne } = useClaimSummaryContext();
  const { handleSubmit } = formMethods;
  const toast = useRef<Toast>(null);

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (data: UpdateClaimArgs) => {
      return putClaim(id, data);
    },
    onSuccess: (claim) => {
      const statusChanged =
        filterStatus !== 'all' && filterStatus !== claim.status;

      afterUpdate(claim);

      if (statusChanged) {
        removeOne(claim.id);
      } else {
        updateOne(claim);
      }
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Claim Update Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: ClaimForm) => {
    const { logs, contacts, relation, ...otherData } = data;
    const cleanedContacts = contacts?.filter((contact) => contact.name);
    const cleanedLogs = logs?.filter((log) => log.date && log.description);

    mutate({
      ...otherData,
      contacts:
        (cleanedContacts?.length ?? 0) > 0 ? cleanedContacts : undefined,
      logs: (cleanedLogs?.length ?? 0) > 0 ? cleanedLogs : undefined,
    });
  };

  if (isSuccess) {
    return (
      <div className="w-full mx-auto flex flex-column gap-5">
        <div className="w-fit mx-auto flex flex-column">
          <div className="text-center text-40px font-bold text-green-500 mb-3">
            Success!
          </div>
          <i className="pi pi-check-circle mx-auto text-40px text-green-500 mb-3" />
          <div className="text-standard mt-3 mx-auto">
            The Claim has been updated successfully!
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className="w-full mx-auto flex flex-column gap-5">
      <ClaimsForm showUpdateFields={true} prevAttachment={prevAttachment} />
      <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
        <ConfirmationDialog
          Button={
            <LoadingButton
              isLoading={isPending}
              label="Update"
              className="w-8rem bluwaiBlue border-0 flex m-auto py-3"
              fontSize="text-20px"
              type="button"
            />
          }
          onConfirm={handleSubmit(onSubmit)}
          message="Are you sure you want to update the claim?"
        />
      </div>
      <ErrorToast toastRef={toast} />
    </form>
  );
};

export default UpdateClaim;
